import { createDefaultStorage, shouldSend, type ThrottleDeps } from './errorThrottle'
import { resolveThrottleConfig } from './errorThrottle.config'

/** Core sources are spelled out for consistent Datadog filtering; feature names
 *  are allowed via the open-ended string member. */
export type ErrorSource = 'react-boundary' | 'api' | 'manual' | (string & {})

export interface ErrorPayload {
  /** Human-readable summary; also the default grouping key. */
  message: string
  /** The underlying Error, when available — preferred by Datadog for stack grouping. */
  error?: Error
  /** Where it came from: a core source or a feature name. */
  source?: ErrorSource
  /** Extra structured context attached to the Datadog error. */
  context?: Record<string, unknown>
  /** Override the grouping key so dynamic messages (ids, filenames) still dedupe. */
  fingerprint?: string
}

const EVENT = 'error'
const errorBus = new EventTarget()

// Resolved once per tab load — config comes from build-time env (see config file).
const config = resolveThrottleConfig()
const throttleDeps: ThrottleDeps = { now: () => Date.now(), storage: createDefaultStorage() }

// Stable grouping key: explicit override, else message + first stack frame so the
// same throw from the same place collapses regardless of dynamic suffixes.
const buildFingerprint = (payload: ErrorPayload): string => {
  if (payload.fingerprint) return payload.fingerprint
  const firstFrame = payload.error?.stack?.split('\n')[1]?.trim()

  return firstFrame ? `${payload.message}::${firstFrame}` : payload.message
}

/**
 * The single entry point for all error reporting — callers never import Datadog.
 * Applies the cross-tab burst+cooldown throttle, then logs each survivor to the
 * console (`[DD] …`, so it's visible in DevTools incl. local dev) and dispatches
 * it to subscribers (the Datadog forwarder).
 */
export function emitError(payload: ErrorPayload): void {
  if (!shouldSend(buildFingerprint(payload), config, throttleDeps)) return

  const label = `[DD]${payload.source ? ` ${payload.source}` : ''}: ${payload.message}`
  console.error(label, ...[payload.error, payload.context].filter(Boolean))

  errorBus.dispatchEvent(new CustomEvent<ErrorPayload>(EVENT, { detail: payload }))
}

// Non-Error throwables (e.g. the response body orvalFetcher throws) would otherwise
// stringify to "[object Object]"; JSON keeps the fields, String() covers what JSON can't.
const stringifyError = (value: unknown): string => {
  if (typeof value === 'string') return value
  try {
    const json = JSON.stringify(value)
    if (json && json !== '{}') return json
  } catch {
    // ignore
  }

  return String(value)
}

/** Ergonomic helper for try/catch sites: `captureError(err, { source })`. */
export function captureError(
  error: unknown,
  opts: Omit<ErrorPayload, 'error' | 'message'> & { message?: string } = {},
): void {
  const err = error instanceof Error ? error : new Error(stringifyError(error))
  emitError({ ...opts, error: err, message: opts.message ?? err.message })
}

export function subscribeErrors(handler: (payload: ErrorPayload) => void): () => void {
  const listener = (event: Event) => handler((event as CustomEvent<ErrorPayload>).detail)
  errorBus.addEventListener(EVENT, listener)

  return () => errorBus.removeEventListener(EVENT, listener)
}
